// src/pages/admin/UtilizadoresFuncoes.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "../components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import EditIcon from "@mui/icons-material/Edit"; // (não usado por agora; fica para extensões futuras)
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";

// ---- filtros por coluna
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// ---- persistência de filtros
const FILTERS_KEY = "tbl:utilizadoresfuncoes:filters";
const KEEP_KEY = "tbl:utilizadoresfuncoes:keep";

export default function UtilizadoresFuncoes() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [data, setData] = useState([]);
  const [utilizadores, setUtilizadores] = useState([]);
  const [funcoes, setFuncoes] = useState([]);

  const initKeep = (() => {
    try {
      const v = localStorage.getItem(KEEP_KEY);
      return v === null ? true : JSON.parse(v);
    } catch {
      return true;
    }
  })();
  const [keepFilters, setKeepFilters] = useState(initKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  const carregar = async () => {
    const [rows, err] = await handleApi(api.post("UtilizadoresFuncoes/SelUtilizadoresFuncoes", {}));
    if (err) return showAlert(err, "error");
    setData(Array.isArray(rows) ? rows : []);
  };

  const carregarCombos = async () => {
    const [u, errU] = await handleApi(api.post("UtilizadoresFuncoes/SelUtilizadoresCombo", {}));
    if (errU) return showAlert(errU, "error");
    const [f, errF] = await handleApi(api.post("UtilizadoresFuncoes/SelFuncoesCombo", {}));
    if (errF) return showAlert(errF, "error");
    setUtilizadores(Array.isArray(u) ? u : []);
    setFuncoes(Array.isArray(f) ? f : []);
  };

  useEffect(() => {
    (async () => {
      await carregar();
      await carregarCombos();
    })();
  }, []);

  // ---- dialog inserir
  const [openAdd, setOpenAdd] = useState(false);
  const [novo, setNovo] = useState({ idutilizador: "", idfuncao: "" });

  const inserir = async () => {
    if (!novo.idutilizador || !novo.idfuncao) {
      showAlert("Seleciona Utilizador e Função.", "warning");
      return;
    }
    const body = {
      idutilizador: Number(novo.idutilizador),
      idfuncao: Number(novo.idfuncao),
    };
    const [_, err, meta] = await handleApi(
      api.post("UtilizadoresFuncoes/InsUtilizadorFuncao", body)
    );
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    setOpenAdd(false);
    setNovo({ idutilizador: "", idfuncao: "" });
  };

  // ---- confirmação eliminar
  const [openConfirm, setOpenConfirm] = useState(false);
  const [alvo, setAlvo] = useState(null);

  const pedirConfirmacao = (row) => {
    setAlvo(row);
    setOpenConfirm(true);
  };
  const fecharConfirmacao = () => {
    setOpenConfirm(false);
    setAlvo(null);
  };

  const confirmarEliminar = async () => {
    if (!alvo?.id) return fecharConfirmacao();
    const [_, err, meta] = await handleApi(
      api.post("UtilizadoresFuncoes/DelUtilizadorFuncao", { id: alvo.id })
    );
    if (err) showAlert(err, "error");
    else if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    fecharConfirmacao();
  };

  // ---- colunas da tabela
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        minWidth: 56,
        maxWidth: 96,
        Cell: ({ row }) => {
          const a = row.original;
          const [anchor, setAnchor] = useState(null);
          const open = Boolean(anchor);
          const handleOpen = (e) => setAnchor(e.currentTarget);
          const handleClose = () => setAnchor(null);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              <IconButton
                color="error"
                size="small"
                disabled={!temPermissao("Menu Administração", "d")}
                onClick={() => pedirConfirmacao(a)}
                title="Eliminar"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>

              {/* <IconButton size="small" onClick={handleOpen} title="Mais ações">
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={anchor} open={open} onClose={handleClose}>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    showAlert("Mais ações (breve)");
                  }}
                >
                  Brevemente
                </MenuItem>
              </Menu> */}
            </div>
          );
        },
      },
      { Header: "Utilizador", accessor: "utilizador", Filter: TextFilter },
      { Header: "Função", accessor: "funcao", Filter: TextFilter },
    ],
    [temPermissao]
  );

  const table = useTable(
    {
      columns,
      data,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              {/* <MDTypography variant="h6">Utilizadores ↔ Funções</MDTypography> */}
              <MDBox display="flex" alignItems="center" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />
                <MDButton
                  color="success"
                  disabled={!temPermissao("Menu Administração", "w")}
                  onClick={() => setOpenAdd(true)}
                >
                  Nova Associação
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            width: col.id === "acoes" ? col.width : undefined,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              padding: "8px 10px",
                              borderTop: "1px solid #eee",
                              fontSize: 12,
                              color: "#1a2b4c",
                              width: cell.column.id === "acoes" ? cell.column.width : undefined,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Inserir */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Associação</DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Utilizador</div>
              <Autocomplete
                size="small"
                options={utilizadores}
                getOptionLabel={(o) => o.designacao || ""}
                value={utilizadores.find((u) => String(u.id) === String(novo.idutilizador)) || null}
                onChange={(_, v) => setNovo((p) => ({ ...p, idutilizador: v?.id || "" }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione…"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                      },
                    }}
                  />
                )}
                disableClearable
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Função</div>
              <Autocomplete
                size="small"
                options={funcoes}
                getOptionLabel={(o) => o.designacao || ""}
                value={funcoes.find((f) => String(f.id) === String(novo.idfuncao)) || null}
                onChange={(_, v) => setNovo((p) => ({ ...p, idfuncao: v?.id || "" }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione…"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                      },
                    }}
                  />
                )}
                disableClearable
              />
            </div>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAdd(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={inserir} color="success">
            Inserir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminação */}
      <Dialog open={openConfirm} onClose={fecharConfirmacao} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent>
          Eliminar a associação <strong>{alvo ? `${alvo.utilizador} — ${alvo.funcao}` : ""}</strong>
          ?
        </DialogContent>
        <DialogActions>
          <MDButton onClick={fecharConfirmacao} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={confirmarEliminar} color="error">
            Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
